import {useEffect, useState} from 'react';
import styles from './AddDeposit.module.scss';
import {apr, dateIcon, errorIcon, greenArrow, lockMini, mellIcon} from '../../../../assets';

import {AddDepositService} from '../../api/add-deposit-service';
import Loader from '../../../../shared/ui/Loader/Loader';
import {useAccount} from 'wagmi';
import {contractABI, moonABI} from '../../../../shared/api/contract/config';

import {ConnectorType, useAppKitProvider} from '@reown/appkit/react';
import {getDateFromLockType} from '../../../../shared/utils/dates';
import ShodawButton from '../../../../shared/ui/ShadowButton/ShodawButton';
import MessageStick from '../../../../shared/ui/MessageStick/MessageStick';

import {formatNumberString} from '../../../../utils/helper';
import {useNavigate} from 'react-router';
import {MoonService} from '../../../../shared/api/contract/ethers/moon';
import {MoonStaking} from '../../../../shared/api/contract/ethers/moon-staking';
import {MainPoolInfo} from "../../../../shared/api/contract";
import {useTranslation} from "react-i18next";

const AddDeposit = () => {
    const [amount, setAmount] = useState('');
    const [lockType, setLockType] = useState<0 | 1 | 2>(1);
    const [pendingBalance, setPendingBalance] = useState(true);
    const [pending, setPending] = useState(false);
    const [generalSettings, setGeneralSettings] = useState<{ minTokens: string }>();
    const [mainPoolInfo, setMainPoolInfo] = useState<MainPoolInfo>();
    const [balance, setBalance] = useState('0');
    const [danger, setDanger] = useState(false);
    const [warning, setWarning] = useState(false);
    const {address} = useAccount();
    const [msg, setMsg] = useState('');
    const navigate = useNavigate();
    const {t} = useTranslation()

    const {walletProvider} = useAppKitProvider<ConnectorType>('eip155');

    const addDepositService = address
        ? new AddDepositService(import.meta.env.VITE_CONTARCT_ADDRESS, contractABI, walletProvider, address)
        : undefined;
    const moonService = address ? new MoonService(import.meta.env.VITE_MOON_ADDRESS, moonABI, walletProvider, address) : undefined;
    const moonStackingService = address ? new MoonStaking(import.meta.env.VITE_CONTARCT_ADDRESS, contractABI, walletProvider, address) : undefined;

    const validateAmount = (amount: number, minAmount: number): boolean => amount >= minAmount;
    const validateBalance = (amount: number, balance: number): boolean => amount <= balance;

    const checkAccess = (): boolean => {
        if (!pending && Number(amount.replace(/\s/g, '')) && !pendingBalance && generalSettings && validateBalance(Number(amount.replace(/\s/g, '')), Number(balance))) return true;
        return false;
    };

    useEffect(() => {
        if (!address || !moonService) return;

        async function main() {
            if (!address || !moonService || !addDepositService || !moonStackingService) return;
            try {
                if (!address) return;

                const balance = await moonService.getMoonBalance();
                if (balance) setBalance(balance);

                const generalSettingResponse = await moonStackingService.getGeneralSettings();

                const poolInfo = await moonStackingService.getMainPoolInfo();

                if (poolInfo) setMainPoolInfo((poolInfo))

                if (generalSettingResponse)
                    setGeneralSettings({minTokens: `${Number(generalSettingResponse.minDepositAmount) / 1000000000000000000}`});
            } catch (error) {
                console.log(error);
            } finally {
                setPendingBalance(false);
            }
        }

        main();
    }, [address]);

    async function handleCreate() {
        if (!address || !moonService || !addDepositService) return;

        if (generalSettings && !validateAmount(Number(amount.replace(/\s/g, '')), Number(generalSettings?.minTokens))) {
            setDanger(true);
            return;
        }

        if (!validateBalance(Number(amount.replace(/\s/g, '')), Number(balance))) {
            setWarning(true);
            return;
        }

        setPending(true);

        try {
            if (!address || !moonService || !addDepositService) return;

            const isApprove = await moonService.allowance(import.meta.env.VITE_CONTARCT_ADDRESS, Number(amount.replace(/\s/g, '')), (m) => setMsg(m));

            if (!isApprove) {
                setPending(false);
                setMsg('');
                return;
            }

            const isCreated = await addDepositService.createDeposit(`${Number(amount.replace(/\s/g, ''))}`, lockType, address, (e) => setMsg(e));

            if (isCreated) navigate('/contract');
            else {
                setPending(false);
                setMsg('');
            }
        } catch (error) {
            console.log(error);
            console.log(t('error_add_deposit'));
            setMsg('');
            setPending(false);
        }
    }

    return (
        <>
            {pendingBalance ? (
                <Loader/>
            ) : (
                <div className={styles.addDeposit}>
                    <div className={styles.amount}>
                        <div className={styles.header}>
                            <div className={styles.address}>
                                <h1>{t('on_wallet')} {`${address?.slice(0, 5)}...${address?.slice(-5)}`}</h1>
                            </div>

                            <div className={styles.balance}>
                                <h2>{`${formatNumberString(balance)}`}</h2>
                                <h3>$MOON</h3>
                            </div>
                        </div>

                        <div className={styles.value}>
                            <div className={styles.data}>
                                <img
                                    src={mellIcon}
                                    alt=""
                                />
                                <input
                                    className={danger || warning ? styles.danger : ''}
                                    autoFocus
                                    placeholder="0"
                                    type="text"
                                    value={amount}
                                    min="0"
                                    step="any"
                                    onChange={(e) => {
                                        setDanger(false);
                                        setWarning(false);
                                        setAmount(Number(e.target.value) <= 0 ? '' : e.target.value.replace(/\D/g, '').replace(/(\d)(?=(\d{3})+$)/g, '$1 '));
                                    }}
                                />
                            </div>

                            <div
                                onClick={() => {
                                    setAmount(String(parseInt(balance)).replace(/\D/g, '').replace(/(\d)(?=(\d{3})+$)/g, '$1 '));
                                    if (generalSettings && !validateAmount(Number(balance), Number(generalSettings.minTokens))) setDanger(true);
                                }}
                                className={styles.max}>
                                {t('max')}
                            </div>
                        </div>
                    </div>

                    {(danger || warning) && (
                        <MessageStick
                            style={{marginTop: '-16px'}}
                            icon={errorIcon}
                            animation
                            title={
                                danger
                                    ? t('min_amount_deposit', {
                                        amount: Number(generalSettings?.minTokens ?? 0).toLocaleString() ?? 0
                                    })
                                    : warning
                                        ? t('not_money')
                                        : ''
                            }
                            bg="rgba(255, 78, 99, 0.05)"
                        />
                    )}

                    <div className={styles.multi}>
                        <div
                            onClick={() => setLockType(0)}
                            className={`${styles.inner} ${lockType === 0 ? styles.active : ''}`}>
                            {t('mon', {mon: 3})}
                        </div>
                        <div
                            onClick={() => setLockType(1)}
                            className={`${styles.inner} ${lockType === 1 ? styles.active : ''}`}>
                            {t('mon', {mon: 6})} <span>x1.5</span>
                        </div>
                        <div
                            onClick={() => setLockType(2)}
                            className={`${styles.inner} ${lockType === 2 ? styles.active : ''}`}>
                            {t('mon', {mon: 12})} <span>x2</span>
                        </div>
                    </div>

                    <div className={styles.line}></div>

                    <div className={styles.info}>
                        <div className={styles.title}>
                            <img
                                src={dateIcon}
                                alt=""
                            />
                            <p>{t('lock_date')}</p>
                        </div>
                        <h2>{getDateFromLockType(lockType)}</h2>
                    </div>


                    {
                        mainPoolInfo && (
                            <div className={styles.info}>
                                <div className={styles.aprTitle}>
                                    <img
                                        src={apr}
                                        alt=""
                                    />
                                    <p>APR</p>
                                </div>
                                <span className={styles.percent}>
                                    {mainPoolInfo && Number(mainPoolInfo.rewardPerSecond) > 0 && Number(mainPoolInfo.totalWeight) > 0 ? (Number(mainPoolInfo.rewardPerSecond) * 31_536_000 / Number(mainPoolInfo.totalWeight) * 100).toFixed(2) : 0}%                                    <img
                                    src={greenArrow} alt=""/>
                                </span>
                            </div>
                        )
                    }

                    <ShodawButton
                        disabled={!checkAccess()}
                        onClick={() => checkAccess() && handleCreate()}
                        title={t(lockType === 0 ? 'lock_btn_deposit_one' : 'lock_btn_deposit_other', {month: lockType === 0 ? '3' : lockType === 1 ? '6' : '12'})}
                        icon={lockMini}
                    />

                    {pending && (
                        <div className="shadow">
                            <Loader/>

                            <h1 dangerouslySetInnerHTML={{__html: msg}}></h1>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default AddDeposit;
